import React, { useEffect, useState } from 'react';
import { orderBy } from 'lodash';
import ReactMarkdown from 'react-markdown';

import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import DialogPaper from '../DialogPaper';

import * as LoyaltyRewardService from '../../api/loyaltyRewardsService';
import * as RewardService from '../../api/rewardService';

const useMilestoneStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    marginLeft: theme.spacing(),
  },
  image: {
    width: 90,
    height: 90,
    marginRight: theme.spacing(3),
    borderRadius: 2,
    objectFit: 'cover',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  descriptionTruncate: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    maxWidth: 320,
  },
  stamps: {
    fontWeight: theme.typography.fontWeightBold,
  },
  stampText: {
    color: theme.palette.primary.main,
  },
  title: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
}));

/**
 * Milestone renders the milestone list item
 * @param milestone
 */
export function MilestoneListItem({
  milestone,
}: {
  milestone: LoyaltyRewardService.RedemptionMilestone;
}) {
  const classes = useMilestoneStyles();
  const [reward, setReward] = useState<RewardService.Reward | null>(null);

  useEffect(() => {
    const fetchMilestoneReward = async () => {
      const res = await RewardService.get(milestone.reward.id);
      setReward(res);
    };

    fetchMilestoneReward();
  }, [milestone]);

  if (!reward) {
    return null;
  }

  return (
    <div className={classes.root}>
      {reward && reward.images && reward.images[0].versions.xl && (
        <div>
          <img
            src={reward.images[0].versions.xl}
            alt={reward.title}
            className={classes.image}
          />
        </div>
      )}
      <div className={classes.content}>
        <Typography variant="h6" className={classes.stampText}>
          {milestone.threshold} Stamp{milestone.threshold > 1 ? 's' : ''}
        </Typography>
        <Typography variant="subtitle2" className={classes.stamps}>
          {reward.title}
        </Typography>
        <ReactMarkdown className={classes.descriptionTruncate}>
          {reward.what}
        </ReactMarkdown>
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  toolbar: {
    height: 70,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
  },
  box: {
    flex: 1,
    display: 'flex',
  },
  close: {
    marginRight: 'auto',
  },
  actions: {
    marginLeft: 'auto',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  subTitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(4),
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  selectedMerchantText: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
    textAlign: 'left',
  },
  unselectedMerchantText: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'left',
  },
  homeContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(),
    marginTop: theme.spacing(4),
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  avatar: {
    height: 80,
    width: 80,
    boxShadow: '0 2px 10px rgba(199, 199, 199, 1)',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(4),
  },

  listItemContainer: {
    marginLeft: 0,
    transition: 'none !important',
    backgroundColor: 'inherit !important',
  },
}));

type Props = Pick<DialogProps, 'open' | 'onClose'> & {
  milestoneList: LoyaltyRewardService.RedemptionMilestone[];
  stampCardName: string;
};

export default function ViewMoreRewardsDialog({
  onClose,
  open,
  milestoneList,
  stampCardName,
}: Props) {
  const classes = useStyles();

  const handleClose = () => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperComponent={DialogPaper}
    >
      <div className={classes.toolbar}>
        <div className={classes.homeContainer}>
          <Typography variant="h4" className={classes.title}>
            {stampCardName}
          </Typography>
        </div>

        <IconButton onClick={handleClose}>
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </div>
      <>
        <Typography variant="h5" className={classes.subTitle}>
          Rewards to Earn
        </Typography>
        <List>
          {orderBy(milestoneList, ['threshold'], ['asc']).map(
            (milestone, idx, arr) => (
              <React.Fragment key={milestone.threshold}>
                <ListItem className={classes.listItemContainer}>
                  <div className={classes.contentContainer}>
                    <MilestoneListItem milestone={milestone} />
                  </div>
                </ListItem>
                {idx < arr.length - 1 && (
                  <Divider
                    variant="fullWidth"
                    style={{ backgroundColor: '#e0e0e0' }}
                  />
                )}
              </React.Fragment>
            ),
          )}
        </List>
      </>
    </Dialog>
  );
}
